"use client";

import React, {
    useEffect,
    useLayoutEffect,
    useCallback,
    useRef,
    useState,
} from "react";
import { useMarkupStore } from "../store";
import type { FeedbackItem, MarkupConfig } from "../types";
import { cn, isSamePage } from "../utils";
import { matchesShortcut } from "../utils/shortcuts";
import {
    buildMarkupMarkdown,
    downloadMarkdown,
    markupMarkdownFilename,
} from "../utils/markdownExport";
import {
    clearPendingFocus,
    pageUrlHere,
    readPendingFocus,
    scrollToPin,
    scrollToPinWhenSettled,
    setPendingFocus,
} from "../utils/focusNavigation";
import { useCurrentUrl } from "../hooks/useCurrentUrl";
import { FeedbackForm } from "./FeedbackForm";
import { FeedbackList } from "./FeedbackList";
import { FeedbackDetail } from "./FeedbackDetail";
import { AuthForm } from "./AuthForm";
import { ViewportControls } from "./ViewportControls";
import { DeviceStage } from "./DeviceStage";
import { useDeviceFrameStore } from "../utils/deviceFrame";
import { FeedbackPins } from "./FeedbackPins";
import { TeamPanel } from "./TeamPanel";
import { PortalHostContext } from "./WidgetPortal";
import { isFromWidget } from "../utils/widgetEvents";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { clearTicketLink, readTicketLink } from "../utils/shareLink";
import { isAdmin } from "../services/userProfile";
import { MessageIcon, CloseIcon, PlusIcon } from "./Icons";
import { useFirebaseSync } from "../hooks/useFirebaseSync";
import { useSessionRecording } from "../hooks/useSessionRecording";
import { logOut, getCurrentUser } from "../services/auth";
import {
    createFeedback as createFirebaseFeedback,
    newFeedbackId,
    storeFeedbackAssets,
} from "../services/firestore";
import type { FeedbackExtras } from "./FeedbackForm";

// CSS is inlined by rollup-plugin-string at build time and self-injected into
// the document so consumers never need a separate CSS import.
// @ts-ignore
import widgetStyles from "../../dist/styles.css";


export interface MarkupWidgetProps {
    config?: Partial<MarkupConfig>;
}

// Styles have to be in the document *before* the browser paints the widget for
// the first time, otherwise the unstyled markup flashes on screen for a frame
// (a full-width white block, since none of the positioning classes apply yet).
// useLayoutEffect runs after the DOM is written but before paint, which is
// exactly that window. useEffect is the SSR fallback — it never runs on the
// server, where there's nothing to paint anyway.
const useIsomorphicLayoutEffect =
    typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * The widget proper: panel, pins, sync. Loaded on demand by the lightweight
 * `MarkupWidget` shell, so the Firebase SDK and everything else in here only
 * reaches a visitor's browser once the widget is actually used.
 */
const MarkupWidgetApp: React.FC<MarkupWidgetProps> = ({
    config: userConfig,
}) => {
    const {
        isVisible,
        setIsVisible,
        isOpen,
        setIsOpen,
        activeTab,
        setActiveTab,
        selectedFeedback,
        setSelectedFeedback,
        feedbackItems,
        addFeedbackItem,
        removeFeedbackItem,
        updateFeedback,
        config,
        setConfig,
        reset,
        isAuthenticated,
        currentUser,
        viewportMode,
        showPins,
        setShowPins,
        devExport,
        setDevExport,
        isSteppingAside,
    } = useMarkupStore();

    // Initialize Firebase sync
    useFirebaseSync();

    // Ref for the widget root element — used to detect Shadow DOM context.
    const widgetRootRef = useRef<HTMLDivElement>(null);

    // Self-inject widget styles so consumers never need a separate CSS import.
    // Skipped when the widget is mounted inside a Shadow DOM (the standalone
    // entry point handles shadow-root injection separately). Runs before paint
    // — see useIsomorphicLayoutEffect above.
    useIsomorphicLayoutEffect(() => {
        if (typeof document === "undefined") return;
        if (
            widgetRootRef.current &&
            widgetRootRef.current.getRootNode() instanceof ShadowRoot
        ) return;
        const STYLE_ID = "markup-widget-styles";
        if (!document.getElementById(STYLE_ID)) {
            const el = document.createElement("style");
            el.id = STYLE_ID;
            el.textContent = widgetStyles as string;
            document.head.appendChild(el);
        }
    }, []);

    // Initialize Session Recording (Feature 2)
    const { getSessionEvents } = useSessionRecording();
    
    // Track last clicked element for page metadata (component detection)
    const lastClickedElementRef = useRef<Element | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (!isFromWidget(e)) lastClickedElementRef.current = e.target as Element;
        };
        document.addEventListener('click', handleClick, true);
        return () => document.removeEventListener('click', handleClick, true);
    }, []);
    
    // Clicks inside the device preview land in the frame's document, not
    // ours; record those too so component detection works there as well.
    const deviceFrame = useDeviceFrameStore((st) => st.frame);
    const deviceFrameUrl = useDeviceFrameStore((st) => st.url);
    useEffect(() => {
        if (!deviceFrame) return;
        let doc: Document;
        try {
            doc = deviceFrame.document;
        } catch (_) {
            return;
        }
        const handleClick = (e: MouseEvent) => {
            lastClickedElementRef.current = e.target as Element;
        };
        doc.addEventListener("click", handleClick, true);
        return () => {
            try {
                doc.removeEventListener("click", handleClick, true);
            } catch (_) {
                /* frame already gone */
            }
        };
    }, [deviceFrame]);

    useEffect(() => {
        if (userConfig) {
            setConfig(userConfig);
        }
    }, [userConfig, setConfig]);

    // Write every outstanding ticket to a markdown brief, grouped by page, for
    // handing to a coding agent.
    const exportMarkdown = useCallback(() => {
        const markdown = buildMarkupMarkdown(feedbackItems, {
            projectId: config.projectId,
        });
        downloadMarkdown(markdown, markupMarkdownFilename());
    }, [feedbackItems, config.projectId]);

    useEffect(() => {
        // The show/hide shortcut lives in the `MarkupWidget` shell, which is
        // listening before this module has even loaded.
        const handleKeyDown = (e: KeyboardEvent) => {
            // Hidden maintainer tool. The combo is the whole discovery
            // mechanism: it reveals the "Save as .md" button in the list (and
            // writes the file straight away), pressing it again hides it. No
            // affordance exists for anyone who doesn't know it's there.
            if (matchesShortcut(e, config.exportShortcut || "ctrl+shift+e")) {
                e.preventDefault();
                const enabling = !devExport;
                setDevExport(enabling);
                if (enabling) exportMarkdown();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [
        config.exportShortcut,
        devExport,
        setDevExport,
        exportMarkdown,
    ]);

    const handleToggle = useCallback(() => {
        if (!isOpen) {
            setIsOpen(true);
        } else {
            setIsOpen(false);
            setSelectedFeedback(null);
            setActiveTab("create");
        }
    }, [isOpen, setIsOpen, setSelectedFeedback, setActiveTab]);

    const handleSubmitFeedback = useCallback(
        // Throws on failure: the form keeps the draft and shows the error.
        // There used to be a "local storage" fallback here, but local items
        // weren't persisted and the next Firestore snapshot replaced them, so
        // a failed save looked like a success and then the ticket vanished.
        async (feedback: FeedbackItem, extras?: FeedbackExtras) => {
            const user = getCurrentUser();
            if (!user) {
                throw new Error("You’ve been signed out. Sign in again to submit.");
            }
            const id = newFeedbackId();
            const stored = await storeFeedbackAssets(
                { ...feedback, id },
                config.projectId,
                extras?.recording,
            );
            await createFirebaseFeedback(stored, config.projectId, user.uid);
            addFeedbackItem(stored);
            setActiveTab("list");
        },
        [addFeedbackItem, setActiveTab, config.projectId]
    );

    const handleCancel = useCallback(() => {
        if (hasUnsavedChanges) {
            if (!window.confirm("Discard this feedback? Your changes will be lost.")) return;
        }
        setHasUnsavedChanges(false);
        reset();
        setIsOpen(false);
    }, [hasUnsavedChanges, reset, setIsOpen]);

    const handleSelectFeedback = useCallback(
        (feedback: FeedbackItem) => {
            const targetUrl = feedback.pageMetadata?.url;
            const pinMatch = config.pinMatch ?? "pathname";
            // With the preview open, "here" is whatever page the preview is
            // showing, and that's the one to navigate.
            const previewing = !!(viewportMode && deviceFrame);
            const hereUrl =
                previewing && deviceFrameUrl
                    ? deviceFrameUrl
                    : window.location.href;
            const isElsewhere =
                !!targetUrl && !isSamePage(targetUrl, hereUrl, pinMatch);
            // Its page on this site — a ticket raised on staging opens on
            // localhost while developing, not back on staging.
            const destination = isElsewhere
                ? pageUrlHere(targetUrl!, pinMatch)
                : null;

            // A ticket raised on another page can't be scrolled to from here —
            // scrolling the current page just moves it to a meaningless offset.
            // Go to the page it belongs to first, and leave a note so the ticket
            // re-opens and centres on its pin once we land.
            if (destination) {
                setPendingFocus(feedback.id, destination);

                if (previewing) {
                    // The preview has its own history; the host's router only
                    // drives the outer page.
                    deviceFrame!.location.assign(destination);
                } else if (config.onNavigate) {
                    // Host router — the SPA stays alive, so the restore effect
                    // below picks it up on the URL change.
                    config.onNavigate(destination);
                } else {
                    window.location.assign(destination);
                }
                return;
            }

            setSelectedFeedback(feedback);

            // Scroll to the pin, using the same resolver the overlay uses so
            // the viewport centres on where the pin is actually drawn. Skipped
            // for a ticket on another environment under exact matching —
            // there's nothing here to scroll to.
            if (!isElsewhere) scrollToPin(feedback);
        },
        [
            setSelectedFeedback,
            config,
            viewportMode,
            deviceFrame,
            deviceFrameUrl,
        ]
    );

    const handlePinClick = useCallback(
        (feedback: FeedbackItem) => {
            setIsOpen(true);
            setActiveTab("list");
            setSelectedFeedback(feedback);

            // Pins only render for the current page, so this is always a
            // same-page scroll.
            scrollToPin(feedback);
        },
        [setIsOpen, setActiveTab, setSelectedFeedback]
    );

    // Tracks SPA navigation as well as full loads, so a jump made through the
    // host's router is noticed too.
    const pageUrl = useCurrentUrl();
    // The page being marked up: the preview's, while it's open and loaded.
    const currentUrl =
        viewportMode && deviceFrame && deviceFrameUrl ? deviceFrameUrl : pageUrl;

    // Land the cross-page jump started in handleSelectFeedback: once we're on
    // the right page and the ticket has arrived from Firestore, re-open it and
    // centre on its pin.
    useEffect(() => {
        const pending = readPendingFocus();
        if (!pending) return;

        const pinMatch = config.pinMatch ?? "pathname";
        if (!isSamePage(pending.url, currentUrl, pinMatch)) return;

        // Feedback arrives from Firestore after auth resolves, so on a full
        // page load the ticket usually isn't in the store yet. Leave the note
        // in place and let the next update re-run this.
        const target = feedbackItems.find((item) => item.id === pending.id);
        if (!target) return;

        clearPendingFocus();
        setIsVisible(true);
        setIsOpen(true);
        setActiveTab("list");
        setSelectedFeedback(target);

        return scrollToPinWhenSettled(target);
    }, [
        feedbackItems,
        currentUrl,
        config.pinMatch,
        setIsVisible,
        setIsOpen,
        setActiveTab,
        setSelectedFeedback,
    ]);

    // ── Ticket links (#markup-ticket=<id>) ─────────────────────────────────
    // Opens the ticket from the live list. Signed out, the widget asks you to
    // sign in first and keeps the link, so the ticket opens once you're in.
    const [linkNotice, setLinkNotice] = useState<string | null>(null);
    const [authResolved, setAuthResolved] = useState(false);
    useEffect(() => {
        // Give Firebase a moment to restore a session before deciding the
        // visitor is signed out.
        const timer = window.setTimeout(() => setAuthResolved(true), 1500);
        return () => window.clearTimeout(timer);
    }, []);

    useEffect(() => {
        const id = readTicketLink();
        if (!id) return undefined;

        if (isAuthenticated) {
            setLinkNotice(null);
            const target = feedbackItems.find((item) => item.id === id);
            if (!target) return undefined; // Not loaded yet; re-runs when it is.
            clearTicketLink();
            setIsVisible(true);
            setIsOpen(true);
            setActiveTab("list");
            setSelectedFeedback(target);
            return scrollToPinWhenSettled(target);
        }

        if (!authResolved) return undefined;
        setIsVisible(true);
        setIsOpen(true);
        setLinkNotice("Sign in to open the ticket you were linked to.");
        return undefined;
    }, [
        isAuthenticated,
        authResolved,
        feedbackItems,
        setIsVisible,
        setIsOpen,
        setActiveTab,
        setSelectedFeedback,
    ]);

    // Detail follows the live list, so other people's changes appear as
    // they happen.
    const liveSelected = selectedFeedback
        ? feedbackItems.find((item) => item.id === selectedFeedback.id) ??
          selectedFeedback
        : null;

    const [showTeam, setShowTeam] = useState(false);
    // Full-screen overlays render here: inside the style scope, outside the
    // panel's transform. See WidgetPortal.
    const [portalHost, setPortalHost] = useState<HTMLDivElement | null>(null);
    // Phones and small tablets get a bottom sheet instead of the side panel.
    const isCompact = useMediaQuery("(max-width: 640px)");

    const handleBackFromDetail = useCallback(() => {
        setSelectedFeedback(null);
    }, [setSelectedFeedback]);

    const handleLogout = useCallback(async () => {
        try {
            await logOut();
            reset();
        } catch (error) {
            console.error("Logout failed:", error);
        }
    }, [reset]);

    const handleAuthSuccess = useCallback(() => {
        setActiveTab("create");
    }, [setActiveTab]);

    // The ticket and its files are already gone from Firebase by the time
    // FeedbackDetail calls this; it only drops the local copy.
    const handleDeleteFeedback = useCallback((id: string) => {
        removeFeedbackItem(id);
        setSelectedFeedback(null);
    }, [removeFeedbackItem, setSelectedFeedback]);

    const handleUpdateFeedback = useCallback((updatedFeedback: FeedbackItem) => {
        updateFeedback(updatedFeedback);
        // Also update selected feedback if it's the same one (it should be)
        setSelectedFeedback(updatedFeedback);
    }, [updateFeedback, setSelectedFeedback]);

    const openFeedbackCount = feedbackItems.filter(
        (f) => f.status === "open"
    ).length;
    // Closed tickets are hidden from the list, so counting them in the tab
    // label would promise rows that aren't there.
    const activeFeedbackCount = feedbackItems.filter(
        (f) => f.status !== "closed"
    ).length;

    // `primaryColor` tints the header and send button. These variables were
    // defined but never applied, so the option did nothing and the send
    // button (white icon on the variable) was invisible. Only set when the
    // host asks for a colour; otherwise the header stays white and accents
    // fall back to the brand pink.
    const primaryColor = userConfig?.primaryColor;
    const widgetStyle: React.CSSProperties = primaryColor
        ? {
              ["--markup-primary" as string]: primaryColor,
              ["--markup-primary-hover" as string]: adjustColor(primaryColor, -20),
          }
        : {};

    const switchToList = () => {
        if (activeTab === "create" && hasUnsavedChanges) {
            if (!window.confirm("Discard this feedback? Your changes will be lost.")) return;
            setHasUnsavedChanges(false);
            reset();
        }
        setActiveTab("list");
    };

    const tabClass = (active: boolean) =>
        cn(
            "flex-1 py-3 px-4 border border-b-0 border-gray-200 rounded-t-xl hover:bg-gray-100 cursor-pointer text-sm! font-medium transition-all flex items-center justify-center gap-1",
            active ? "bg-gray-200" : "border-white",
        );

    const header = (
        <div
            className="flex items-center justify-between gap-2 px-5 py-4 text-black"
            style={{ backgroundColor: "var(--markup-primary, #ffffff)" }}
        >
            <h2 className="text-base! font-semibold m-0 truncate">
                {config.labels?.feedbackTitle || "Feedback"}
            </h2>
            <div className="flex items-center gap-2 shrink-0">
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowPins(!showPins);
                    }}
                    aria-pressed={showPins}
                    className="p-2 text-xs! font-medium bg-gray-50 border border-gray-200 text-black rounded-lg hover:bg-gray-100 cursor-pointer transition-all flex items-center gap-1.5"
                    title={showPins ? "Hide pins" : "Show pins"}
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {showPins ? "Hide pins" : "Show pins"}
                </button>
                {isAdmin(currentUser) && (
                    <button
                        type="button"
                        onClick={() => setShowTeam(true)}
                        className="text-xs! py-2 px-2 bg-gray-50 border border-gray-200 text-gray-900 rounded-lg hover:bg-gray-200 cursor-pointer transition-all"
                    >
                        Team
                    </button>
                )}
                {isAuthenticated && currentUser && (
                    <button
                        type="button"
                        onClick={handleLogout}
                        className="text-xs! py-2 px-2 bg-gray-50 border border-gray-200 text-gray-900 rounded-lg hover:bg-gray-200 cursor-pointer transition-all"
                    >
                        Sign out
                    </button>
                )}
                {isCompact && (
                    <button
                        type="button"
                        onClick={handleToggle}
                        aria-label="Close feedback"
                        className="p-2 bg-gray-50 border border-gray-200 rounded-lg"
                    >
                        <CloseIcon className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );

    const body = !isAuthenticated ? (
        <div className="flex-1 overflow-y-auto">
            {linkNotice && (
                <p role="status" className="mx-5 mt-5 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs!">
                    {linkNotice}
                </p>
            )}
            <AuthForm onSuccess={handleAuthSuccess} />
        </div>
    ) : showTeam && isAdmin(currentUser) ? (
        <TeamPanel onBack={() => setShowTeam(false)} />
    ) : liveSelected ? (
        <FeedbackDetail
            feedback={liveSelected}
            onBack={handleBackFromDetail}
            onDelete={handleDeleteFeedback}
            onUpdate={handleUpdateFeedback}
        />
    ) : (
        <>
            <div className="flex border-b border-gray-200 px-5" role="tablist">
                <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "create"}
                    className={tabClass(activeTab === "create")}
                    onClick={() => setActiveTab("create")}
                >
                    <PlusIcon className="w-3.5 h-3.5" />
                    New
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "list"}
                    className={tabClass(activeTab === "list")}
                    onClick={switchToList}
                >
                    View All ({activeFeedbackCount})
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
                {activeTab === "create" && (
                    <FeedbackForm
                        onSubmit={handleSubmitFeedback}
                        onCancel={handleCancel}
                        getSessionEvents={getSessionEvents}
                        lastClickedElement={lastClickedElementRef.current}
                        onDirtyChange={setHasUnsavedChanges}
                    />
                )}
                {activeTab === "list" && (
                    <FeedbackList
                        feedbackItems={feedbackItems}
                        onSelectFeedback={handleSelectFeedback}
                    />
                )}
            </div>
        </>
    );

    const badge = openFeedbackCount > 0 && (
        <span className="absolute -top-3 -left-1 min-w-[24px] h-[24px] px-1 bg-matter-ink text-matter-cream rounded-full text-xs! font-semibold flex items-center justify-center">
            {openFeedbackCount}
        </span>
    );

    return (
        // Zero-size fixed root — all children use position:fixed so they
        // escape this container visually, but they stay DOM-children so the
        // CSS scope selector (.markup-widget-scope) covers them all.
        <div
            className="markup-widget-scope"
            style={{ ...widgetStyle, position: "fixed", top: 0, left: 0, width: 0, height: 0, overflow: "visible", zIndex: 999990, pointerEvents: "none" }}
        >
            <PortalHostContext.Provider value={portalHost}>
            {/* Feedback Pins — each pin sets pointerEvents:auto on its own
                style. While previewing, the page underneath is covered and
                the preview draws its own pins over the device. */}
            {viewportMode ? (
                <DeviceStage onPinClick={handlePinClick} />
            ) : (
                <FeedbackPins onPinClick={handlePinClick} />
            )}

            {/* Viewport Controls — needs pointer-events-auto since it's a direct
                child of the pointer-events:none scope root. Not on phones:
                previewing a phone on a phone isn't useful. */}
            {!isCompact && (
                <div className="pointer-events-auto">
                    <ViewportControls />
                </div>
            )}

            {isCompact ? (
                // ── Phone: floating button + bottom sheet ──
                <div ref={widgetRootRef} className="markup-widget" data-markup-widget aria-hidden={!isVisible}>
                    {isVisible && !isOpen && !isSteppingAside && (
                        <button
                            type="button"
                            onClick={handleToggle}
                            data-markup-pill
                            aria-label="Open feedback"
                            className="fixed bottom-4 right-4 w-14 h-14 rounded-full bg-white shadow-xl flex items-center justify-center pointer-events-auto"
                            style={{ position: "fixed", right: 16, bottom: 16, zIndex: 999999 }}
                        >
                            <MessageIcon className="w-6 h-6 text-black" />
                            {badge}
                        </button>
                    )}
                    <div
                        role="dialog"
                        aria-label={config.labels?.feedbackTitle || "Feedback"}
                        className="bg-white rounded-t-2xl shadow-2xl overflow-hidden flex flex-col text-black pointer-events-auto transition-transform duration-300 ease-in-out"
                        style={{
                            position: "fixed",
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: "88dvh",
                            zIndex: 999999,
                            transform: isVisible && isOpen && !isSteppingAside ? "translateY(0)" : "translateY(100%)",
                            visibility: isVisible && isOpen && !isSteppingAside ? "visible" : "hidden",
                        }}
                    >
                        {header}
                        {body}
                    </div>
                </div>
            ) : (
            /* ── Desktop: side panel with pull tab ──
               The outer row stays pointer-events:none because its layout box
               spans the full panel height even when closed and the panel is
               translated off-screen; without this, hover/click hit-testing
               would cover that whole height instead of just the visible pill,
               blocking clicks on the host page. */
            <div
                ref={widgetRootRef}
                className={cn(
                    "markup-widget fixed z-[999999] right-[0px] top-1/2 flex items-center transition-transform duration-300 ease-in-out pointer-events-none",
                    // Outer box has pointer-events:none (see comment above), so
                    // it can't receive :hover itself — use has-[] to trigger
                    // the peek-out translate from the pill's own hover state.
                    isOpen || !isVisible || isSteppingAside
                        ? ""
                        : "has-[[data-markup-pill]:hover]:translate-x-[-8px]"
                )}
                style={{
                    // Positioning is duplicated inline rather than left to the
                    // classes alone: if the stylesheet is ever a beat late (a
                    // consumer importing styles.css through their own bundler,
                    // say), an unpositioned widget would render full-width in
                    // the page flow. Inline, the transform below always has a
                    // fixed box to move off-screen.
                    position: "fixed",
                    right: 0,
                    top: "50%",
                    zIndex: 999999,
                    display: "flex",
                    alignItems: "center",
                    // Three states: fully off-screen (hidden), pill peeking out,
                    // and the open panel.
                    // Off-screen while a screenshot is taken, too.
                    transform: !isVisible || isSteppingAside
                        // +5px because the unread badge is offset -left-1 (4px)
                        // past the row's left edge, so a plain 100% slide
                        // leaves the badge showing.
                        ? "translateY(-50%) translateX(calc(100% + 5px))"
                        : isOpen
                          ? "translateY(-50%) translateX(0)"
                          : "translateY(-50%) translateX(calc(100% - 40px))",
                }}
                aria-hidden={!isVisible}
                data-markup-widget
            >
                <button
                    type="button"
                    onClick={handleToggle}
                    data-markup-pill
                    aria-expanded={isOpen}
                    aria-label={isOpen ? "Close feedback" : "Open feedback"}
                    className="relative bg-white h-fit flex items-center gap-2 px-2 pr-5 text-black border-none rounded-l-2xl cursor-pointer text-sm! font-semibold transition-all py-5 shadow-lg pointer-events-auto"
                >
                    {isOpen && <CloseIcon className="w-5 h-5" />}
                    <span className="text-black" style={{ writingMode: "sideways-lr" }}>
                        Matter
                    </span>
                    {badge}
                </button>
                <div
                    role="dialog"
                    aria-label={config.labels?.feedbackTitle || "Feedback"}
                    className="bg-white rounded-l-xl shadow-2xl overflow-hidden flex flex-col text-black pointer-events-auto"
                    // Fits short laptop screens instead of cutting off the
                    // header and the submit button.
                    style={{ width: 500, height: "min(750px, calc(100vh - 32px))" }}
                >
                    {header}
                    {body}
                </div>
            </div>
            )}
            </PortalHostContext.Provider>

            <div
                ref={setPortalHost}
                className="markup-widget"
                data-markup-portal
                style={{ pointerEvents: "auto" }}
            />
        </div>
    );
};

function adjustColor(color: string, amount: number): string {
    const clamp = (val: number) => Math.min(255, Math.max(0, val));

    if (color.startsWith("#")) {
        const hex = color.slice(1);
        const num = parseInt(hex, 16);
        const r = clamp((num >> 16) + amount);
        const g = clamp(((num >> 8) & 0x00ff) + amount);
        const b = clamp((num & 0x0000ff) + amount);
        return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
    }

    return color;
}

export default MarkupWidgetApp;
